import React, { useState, useEffect } from 'react'
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Snackbar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  TextField
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { 
  Error as ErrorIcon, 
  TrendingUp, 
  ShoppingCart,
  LocalShipping,
  People,
  AllInbox,
  Warning,
  CalendarToday,
  DateRange,
  Equalizer,
  AttachMoney,
  Receipt
} from '@material-ui/icons'
import CountUp from 'react-countup'
import bilanCommandeService from '../../services/BilanCommandeService'

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: theme.shadows[4]
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px'
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    backgroundColor: '#ffebee',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #f44336'
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    color: '#f44336'
  },
  positive: {
    color: '#4CAF50',
    fontWeight: 'bold'
  },
  negative: {
    color: '#f44336',
    fontWeight: 'bold'
  },
  warning: {
    color: '#FF9800',
    fontWeight: 'bold'
  },
  statCard: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(2)
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& th': {
      color: 'white',
      fontWeight: 'bold'
    }
  },
  select: {
    marginBottom: theme.spacing(2),
    minWidth: 200
  },
  periodeCard: {
    background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
    border: '1px solid #e0e0e0'
  },
  alertCard: {
    borderLeft: '4px solid #f44336',
    backgroundColor: '#ffebee'
  },
  warningCard: {
    borderLeft: '4px solid #FF9800',
    backgroundColor: '#fff3e0'
  },
  chip: {
    margin: theme.spacing(0.5)
  },
  tabs: {
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  montant: {
    fontWeight: 'bold'
  }
}))

function BilanCommande() {
  const classes = useStyles()
  const [bilanData, setBilanData] = useState(null)
  const [evolutionData, setEvolutionData] = useState(null)
  const [fournisseursData, setFournisseursData] = useState(null)
  const [produitsData, setProduitsData] = useState(null)
  const [comparaisonData, setComparaisonData] = useState(null)
  const [alertesData, setAlertesData] = useState(null)
  const [syntheseData, setSyntheseData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [periode, setPeriode] = useState('jour')
  const [typeAnalyse, setTypeAnalyse] = useState('general')
  const [dateDebut, setDateDebut] = useState('')
  const [dateFin, setDateFin] = useState('')
  const [activeTab, setActiveTab] = useState(0)

  const periodes = [
    { key: 'jour', label: "Aujourd'hui", icon: <CalendarToday /> },
    { key: 'hier', label: 'Hier', icon: <CalendarToday /> },
    { key: 'semaine', label: '7 derniers jours', icon: <DateRange /> },
    { key: 'mois', label: 'Mois en cours', icon: <DateRange /> },
    { key: 'annee', label: 'Année en cours', icon: <DateRange /> },
    { key: 'personnalise', label: 'Personnalisé', icon: <DateRange /> }
  ]

  const typesAnalyse = [
    { key: 'general', label: 'Bilan Général', icon: <Equalizer /> },
    { key: 'evolution', label: 'Évolution', icon: <TrendingUp /> },
    { key: 'fournisseurs', label: 'Analyse Fournisseurs', icon: <People /> },
    { key: 'produits', label: 'Analyse Produits', icon: <AllInbox /> },
    { key: 'comparaison', label: 'Commandes vs Livraisons', icon: <LocalShipping /> },
    { key: 'alertes', label: 'Alertes', icon: <Warning /> },
    { key: 'synthese', label: 'Synthèse', icon: <Receipt /> }
  ]

  useEffect(() => {
    fetchData()
  }, [periode, typeAnalyse, dateDebut, dateFin, activeTab])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      let response

      switch (typeAnalyse) {
        case 'general':
          if (periode === 'personnalise' && dateDebut && dateFin) {
            response = await bilanCommandeService.getDetailParDate(dateDebut, dateFin)
          } else {
            response = await bilanCommandeService.getBilanGeneral(periode)
          }
          setBilanData(response.data)
          break

        case 'evolution':
          response = await bilanCommandeService.getEvolution(typeAnalyse)
          setEvolutionData(response.data)
          break

        case 'fournisseurs':
          response = await bilanCommandeService.getAnalyseFournisseurs()
          setFournisseursData(response.data)
          break

        case 'produits':
          response = await bilanCommandeService.getAnalyseProduits()
          setProduitsData(response.data)
          break

        case 'comparaison':
          response = await bilanCommandeService.getCommandesVsLivraisons()
          setComparaisonData(response.data)
          break

        case 'alertes':
          response = await bilanCommandeService.getAlertes()
          setAlertesData(response.data)
          break

        case 'synthese':
          response = await bilanCommandeService.getSynthese()
          setSyntheseData(response.data)
          break

        default:
          break
      }

    } catch (error) {
      console.error('❌ Erreur:', error)
      setError('Erreur lors du chargement des données')
      setSnackbarOpen(true)
    } finally {
      setLoading(false)
    }
  }

  // Composant pour formater les montants avec CountUp
  const FormattedMontant = ({ montant, className = '', positive = false, negative = false }) => {
    const colorClass = positive ? classes.positive : negative ? classes.negative : '';
    return (
      <CountUp 
        start={0} 
        end={montant || 0} 
        duration={1.5} 
        separator=" " 
        suffix=" F CFA"
        className={`${className} ${colorClass} ${classes.montant}`}
      />
    );
  };

  const formatPourcentage = (valeur) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valeur || 0)
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const handleRetry = () => {
    fetchData()
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Date non disponible';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString; // Retourne la chaîne originale en cas d'erreur
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: '10px' }}>
          Chargement des données...
        </Typography>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h5" gutterBottom style={{ marginBottom: '30px', color: 'gray' }}>
        📦 Bilan des Commandes - Tableau de Bord
      </Typography>

      {/* Sélecteurs */}
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
        <Grid item>
          <FormControl className={classes.select}>
            <InputLabel>Type d'analyse</InputLabel>
            <Select
              value={typeAnalyse}
              onChange={(e) => setTypeAnalyse(e.target.value)}
            >
              {typesAnalyse.map((type) => (
                <MenuItem key={type.key} value={type.key}>
                  <Box display="flex" alignItems="center">
                    {type.icon}
                    <Box ml={1}>{type.label}</Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item>
          <FormControl className={classes.select}>
            <InputLabel>Période</InputLabel>
            <Select
              value={periode}
              onChange={(e) => setPeriode(e.target.value)}
            >
              {periodes.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  <Box display="flex" alignItems="center">
                    {p.icon}
                    <Box ml={1}>{p.label}</Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {periode === 'personnalise' && (
          <>
            <Grid item>
              <TextField
                label="Date début"
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                InputLabelProps={{ shrink: true }}
                className={classes.select}
              />
            </Grid>
            <Grid item>
              <TextField
                label="Date fin"
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                InputLabelProps={{ shrink: true }}
                className={classes.select}
              />
            </Grid>
          </>
        )}
      </Grid>

      {error && (
        <div className={classes.errorContainer}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="body1" style={{ flex: 1 }}>
            {error}
          </Typography>
          <Button color="secondary" onClick={handleRetry}>
            Réessayer
          </Button>
        </div>
      )}

      {/* Bilan Général */}
      {typeAnalyse === 'general' && bilanData && (
        <Grid container spacing={3}>
          {/* Cartes principales */}
          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <ShoppingCart style={{ fontSize: 40, color: '#2196F3', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Total Commandes
                </Typography>
                <Typography variant="h4">
                  {bilanData.bilan_general?.total_commandes || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Période: {bilanData.periode}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <AttachMoney style={{ fontSize: 40, color: '#4CAF50', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Montant Total
                </Typography>
                <Typography variant="h4">
                  <FormattedMontant montant={bilanData.bilan_general?.montant_total} positive />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Moyenne: <FormattedMontant montant={bilanData.bilan_general?.montant_moyen} />
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <People style={{ fontSize: 40, color: '#FF9800', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Fournisseurs
                </Typography>
                <Typography variant="h4">
                  {bilanData.bilan_general?.nombre_fournisseurs || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Actifs cette période
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <LocalShipping style={{ fontSize: 40, color: '#9C27B0', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Quantité Livrée
                </Typography>
                <Typography variant="h4">
                  {bilanData.bilan_general?.total_quantite_livree || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Commandée: {bilanData.bilan_general?.total_quantite_commandee || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Fournisseurs */}
          {bilanData.par_fournisseur && bilanData.par_fournisseur.length > 0 && (
            <Grid item xs={12}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📊 Répartition par Fournisseur
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead className={classes.tableHeader}>
                        <TableRow>
                          <TableCell>Fournisseur</TableCell>
                          <TableCell align="right">Nombre de Commandes</TableCell>
                          <TableCell align="right">Montant Total</TableCell>
                          <TableCell align="right">Moyenne/Commande</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bilanData.par_fournisseur.map((fournisseur, index) => (
                          <TableRow key={index}>
                            <TableCell>{fournisseur.nomFournisseur}</TableCell>
                            <TableCell align="right">{fournisseur.nombre_commandes}</TableCell>
                            <TableCell align="right">
                              <FormattedMontant montant={fournisseur.montant_total} />
                            </TableCell>
                            <TableCell align="right">
                              <FormattedMontant montant={fournisseur.montant_moyen} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Évolution */}
      {typeAnalyse === 'evolution' && evolutionData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={classes.periodeCard}>
              <CardContent>
                <Typography variant="h6">
                  📈 Évolution des Commandes
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {evolutionData.map((periode, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                  {formatDate(periode.date)}
                  </Typography>
                  <Typography variant="body2">
                    Commandes: <strong>{periode.nombre_commandes}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Montant: <FormattedMontant montant={periode.montant_total} positive />
                  </Typography>
                  <Typography variant="body2">
                    Moyenne: <FormattedMontant montant={periode.montant_moyen} />
                  </Typography>
                  <Typography variant="body2">
                    Livraison: <strong>{periode.quantite_livree} unités</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Analyse Fournisseurs */}
      {typeAnalyse === 'fournisseurs' && fournisseursData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={classes.periodeCard}>
              <CardContent>
                <Typography variant="h6">
                  👥 Analyse des Fournisseurs
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {fournisseursData.map((fournisseur, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {fournisseur.nomFournisseur}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Commandes: {fournisseur.total_commandes}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Montant: <FormattedMontant montant={fournisseur.montant_total_commandes} positive />
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Moyenne: <FormattedMontant montant={fournisseur.montant_moyen_commande} />
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Livraison: {fournisseur.total_quantite_livree} unités
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Produits: {fournisseur.nombre_produits_differents}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Analyse Produits */}
      {typeAnalyse === 'produits' && produitsData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={classes.periodeCard}>
              <CardContent>
                <Typography variant="h6">
                  📦 Top 50 Produits Commandés
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>Produit</TableCell>
                        <TableCell>Forme</TableCell>
                        <TableCell>Famille</TableCell>
                        <TableCell align="right">Commandes</TableCell>
                        <TableCell align="right">Qté Livrée</TableCell>
                        <TableCell align="right">Qté Commandée</TableCell>
                        <TableCell align="right">Prix Moyen</TableCell>
                        <TableCell align="right">Montant Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {produitsData.map((produit, index) => (
                        <TableRow key={index}>
                          <TableCell>{produit.nomProd}</TableCell>
                          <TableCell>{produit.FormFarmaceutik}</TableCell>
                          <TableCell>{produit.famille}</TableCell>
                          <TableCell align="right">{produit.nombre_commandes}</TableCell>
                          <TableCell align="right">{produit.quantite_totale_livree}</TableCell>
                          <TableCell align="right">{produit.quantite_totale_commandee}</TableCell>
                          <TableCell align="right">
                            <FormattedMontant montant={produit.prix_unitaire_moyen} />
                          </TableCell>
                          <TableCell align="right">
                            <FormattedMontant montant={produit.montant_total} positive />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Commandes vs Livraisons */}
      {typeAnalyse === 'comparaison' && comparaisonData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={classes.periodeCard}>
              <CardContent>
                <Typography variant="h6">
                  ⚖️ Commandes vs Livraisons (6 derniers mois)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {comparaisonData.map((mois, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {mois.mois}
                  </Typography>
                  <Typography variant="body2">
                    Commandes: <strong>{mois.nombre_commandes}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Qté Commandée: <strong>{mois.quantite_commandee}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Qté Livrée: <strong>{mois.quantite_livree}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Taux Livraison: <strong className={
                      mois.taux_livraison > 90 ? classes.positive : 
                      mois.taux_livraison > 70 ? classes.warning : classes.negative
                    }>
                      {formatPourcentage(mois.taux_livraison)}%
                    </strong>
                  </Typography>
                  <Typography variant="body2">
                    Montant: <FormattedMontant montant={mois.montant_total} positive />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Alertes */}
      {typeAnalyse === 'alertes' && alertesData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={classes.periodeCard}>
              <CardContent>
                <Typography variant="h6">
                  ⚠️ Alertes et Problématiques
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Livraison Partielle */}
          <Grid item xs={12} md={6}>
            <Card className={classes.warningCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Livraisons Partielles
                </Typography>
                {alertesData.livraison_partielle && alertesData.livraison_partielle.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Produit</TableCell>
                          <TableCell>Fournisseur</TableCell>
                          <TableCell align="right">Manquant</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {alertesData.livraison_partielle.slice(0, 20).map((alerte, index) => (
                          <TableRow key={index}>
                            <TableCell>{alerte.nomProd}</TableCell>
                            <TableCell>{alerte.nomFournisseur}</TableCell>
                            <TableCell align="right" className={classes.negative}>
                              {alerte.manquant}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2">Aucune livraison partielle</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Expiration */}
          <Grid item xs={12} md={6}>
            <Card className={classes.alertCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Expiration Proche (≤ 30 jours)
                </Typography>
                {alertesData.expiration_proche && alertesData.expiration_proche.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Produit</TableCell>
                          <TableCell>Fournisseur</TableCell>
                          <TableCell align="right">Jours restants</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {alertesData.expiration_proche.slice(0, 20).map((alerte, index) => (
                          <TableRow key={index}>
                            <TableCell>{alerte.nomProd}</TableCell>
                            <TableCell>{alerte.nomFournisseur}</TableCell>
                            <TableCell align="right" className={
                              alerte.jours_restants <= 7 ? classes.negative : classes.warning
                            }>
                              {alerte.jours_restants} jours
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2">Aucune expiration proche</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Synthèse */}
      {typeAnalyse === 'synthese' && syntheseData && (
        <Grid container spacing={3}>
          {/* Cartes synthèse */}
          <Grid item xs={12} md={4}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Aujourd'hui
                </Typography>
                <Typography variant="h4">
                  {syntheseData.aujourdhui?.commandes_ajd || 0}
                </Typography>
                <Typography variant="body2">
                  <FormattedMontant montant={syntheseData.aujourdhui?.montant_ajd} positive />
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Ce Mois
                </Typography>
                <Typography variant="h4">
                  {syntheseData.mois?.commandes_mois || 0}
                </Typography>
                <Typography variant="body2">
                  <FormattedMontant montant={syntheseData.mois?.montant_mois} positive />
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Cette Année
                </Typography>
                <Typography variant="h4">
                  {syntheseData.annee?.commandes_annee || 0}
                </Typography>
                <Typography variant="body2">
                  <FormattedMontant montant={syntheseData.annee?.montant_annee} positive />
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Fournisseurs */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🏆 Top 5 Fournisseurs
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fournisseur</TableCell>
                        <TableCell align="right">Commandes</TableCell>
                        <TableCell align="right">Montant</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {syntheseData.top_fournisseurs && syntheseData.top_fournisseurs.map((fournisseur, index) => (
                        <TableRow key={index}>
                          <TableCell>{fournisseur.nomFournisseur}</TableCell>
                          <TableCell align="right">{fournisseur.nombre_commandes}</TableCell>
                          <TableCell align="right">
                            <FormattedMontant montant={fournisseur.montant_total} positive />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Produits */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📦 Top 5 Produits
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Produit</TableCell>
                        <TableCell align="right">Quantité Livrée</TableCell>
                        <TableCell align="right">Montant</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {syntheseData.top_produits && syntheseData.top_produits.map((produit, index) => (
                        <TableRow key={index}>
                          <TableCell>{produit.nomProd}</TableCell>
                          <TableCell align="right">{produit.quantite_livree}</TableCell>
                          <TableCell align="right">
                            <FormattedMontant montant={produit.montant_total} positive />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Message si pas de données */}
      {!bilanData && !evolutionData && !fournisseursData && !produitsData && 
       !comparaisonData && !alertesData && !syntheseData && !loading && (
        <Grid item xs={12}>
          <Card>
            <CardContent style={{ textAlign: 'center', padding: '40px' }}>
              <Typography variant="h6" color="textSecondary">
                Aucune donnée disponible
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Les données pour cette période sont actuellement vides.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={error}
        action={
          <Button color="inherit" size="small" onClick={handleCloseSnackbar}>
            Fermer
          </Button>
        }
      />
    </div>
  )
}

export default BilanCommande